import { Component } from '@theme/component';

const FG_AGE_CHECK_FREQUENCY_MS = 1000 * 60 * 60 * 24;

/**
 * @typedef {Object} FGWComplianceConfiguration
 * @type {object}
 * @property {string} doNotShipTo - comma separated state abbreviations
 * @property {string} shipTo - comma separated state abbreviations
 * @property {{id: string, states: string[]}[]} warehouses - warehouse abbreviation and the states each one serves
 */


class FGAvailabilityData {
  /** @type {FGWComplianceConfiguration} */
  complianceConfiguration;

  currentState = '';

  constructor() {
    const configEl = document.getElementById('fgw-compliance-configuration');
    if (!configEl) {
      throw new Error('FGAvailabilityData:: Configuration element not found');
    }

    try {
      this.complianceConfiguration = JSON.parse(configEl.textContent);
      this.complianceConfiguration.doNotShipTo = this.complianceConfiguration.doNotShipTo.replace(/\s/g, '');
      this.complianceConfiguration.shipTo = this.complianceConfiguration.shipTo.replace(/\s/g, '');
    } catch (e) {
      console.error('FGAvailabilityData:: Config JSON Parse Failed', e);
      throw new Error('FGAvailabilityData:: Failed to load compliance configuration');
    }

    if (this.complianceConfiguration === null) {
      throw new Error('FGAvailabilityData:: Compliance configuration is null');
    }

    const existingState = window.localStorage.getItem('fgw_state');
    if (existingState && existingState !== '') {
      this.setCurrentState(existingState);
    }
  }

  /**
   * 
   * @param {string} state 
   */
  setCurrentState(state) {
    this.currentState = state;
    console.log(`FGAvailabilityData:: State has been set to "${state}". Warehouse: "${this.getCurrentWarehouse()}"`);
    if (this.isNoShipState()) {
      console.warn(`FGAvailabilityData:: State "${state}" is a no ship state.`);
    }
  }

  isNoShipState() {
    if (this.complianceConfiguration.doNotShipTo.split(',').includes(this.currentState)) {
      return true;
    }
    return false;
  }

  getCurrentState() {
    if (!this.currentState || this.currentState === '') {
      return null;
    }
    return this.currentState;
  }

  getCurrentWarehouse() {
    if (!this.currentState || this.currentState === '') {
      return null;
    }
    const foundStateWH = this.complianceConfiguration.warehouses.find(item => item.states.includes(this.currentState));
    if (!foundStateWH) {
      console.warn(`FGAvailabilityData:: No warehouse found for state "${this.currentState}"`);
      return null;
    }
    return foundStateWH.id;
  }
}

/**
 * @type {FGAvailabilityData}
 */
globalThis.FGAvailabilityData = new FGAvailabilityData();

/**
 * 
 * @typedef {object} FGStateSelectorRefs
 * @property {HTMLInputElement} stateSelect - The state selector
 * @extends {Component<FGStateSelectorRefs>}
 */
class FGStateSelector extends Component {
  requiredRefs = ['stateSelect'];

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    const existingState = window.localStorage.getItem('fgw_state');
    if (existingState && existingState !== '') {
      this.refs.stateSelect.value = existingState;
      this.removeEmptyOption();
    }

    this.handleStateChangeEvent = this.handleStateChangeEvent.bind(this);
    document.addEventListener('fgw_state_change', this.handleStateChangeEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('fgw_state_change', this.handleStateChangeEvent);
  }

  removeEmptyOption() {
    const emptyOption = this.refs.stateSelect.querySelector('option[value=""]');
    if (emptyOption) {
      emptyOption.remove();
    }
  }

  getSelectedState() {
    return this.refs.stateSelect.value;
  }

  /**
   *
   * @param {Event} event
   */
  handleStateChangeEvent(event) {
    if (!(event instanceof CustomEvent)) return;
    if (event.detail.sender !== this.dataset.sender) {
      this.refs.stateSelect.value = event.detail.newState;
    }
    this.removeEmptyOption();
  }

  /**
   *
   * @param {Event} event
   */
  onStateChange({ target }) {
    if (!(target instanceof HTMLSelectElement)) return;
    const newState = target.value;
    window.localStorage.setItem('fgw_state', newState);
    globalThis.FGAvailabilityData.setCurrentState(newState);
    document.dispatchEvent(new CustomEvent('fgw_state_change', {
      detail: {
        newState: newState,
        sender: this.dataset.sender
      }
    }));
  }
}

customElements.define('fg-state-selector', FGStateSelector);

/**
 * 
 * @typedef {object} FGStateAgeRefs
 * @property {HTMLInputElement} stateSelect - The state selector
 * @property {HTMLButtonElement} submitButton - The "Yes I'm 21 or over" button
 * @property {HTMLButtonElement} cancelButton - The "No I'm under 21" button
 * @extends {Component<FGStateAgeRefs>}
 */
class FGStateAgeDialog extends Component {
  requiredRefs = ['stateSelect', 'submitButton', 'cancelButton'];

  ageCheckKey = `fg_last_age_check_${Shopify.shop}`;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    // Retrieve and set the already selected state if it exists
    const existingState = window.localStorage.getItem('fgw_state');
    if (existingState && existingState !== '') {
      this.refs.stateSelect.value = existingState;
      this.removeEmptyOption();
    }

    this.handleStateChangeEvent = this.handleStateChangeEvent.bind(this);
    document.addEventListener('fgw_state_change', this.handleStateChangeEvent);

    // Check for times. Pop the dialog if last checked has expired or we have no state selected
    const lastChecked = window.localStorage.getItem(this.ageCheckKey);
    if (!lastChecked) {
      this.showDialog();
    } else {
      const now = Date.now();
      const lastCheckedNumber = parseInt(lastChecked);
      if ((now - lastCheckedNumber) >= FG_AGE_CHECK_FREQUENCY_MS) {
        this.showDialog();
      } else {
        if (!existingState) {
          this.showDialog();
        }
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('fgw_state_change', this.handleStateChangeEvent);
  }

  removeEmptyOption() {
    const emptyOption = this.refs.stateSelect.querySelector('option[value=""]');
    if (emptyOption) {
      emptyOption.remove();
    }
  }

  getSelectedState() {
    return this.refs.stateSelect.value;
  }

  showDialog() {
    this.setAttribute('data-dialog', 'open');
  }

  hideDialog() {
    this.setAttribute('data-dialog', 'closed');
  }

  /**
   * 
   * @param {string[]} errors 
   * @returns 
   */
  setErrors(errors) {
    if (!errors || errors.length == 0) {
      this.setAttribute('data-error', 'false');
      return;
    }
    const errorContainer = this.querySelector('[data-errors]');
    if (!errorContainer) return;
    const elements = errors.map(error => {
      const el = document.createElement('span');
      el.classList.add('dialog-error');
      el.textContent = error;
      return el;
    });
    errorContainer.innerHTML = "";
    errorContainer.append(...elements);
    this.setAttribute('data-error', 'true');
  }

  /**
   *
   * @param {Event} event
   */
  handleStateChangeEvent(event) {
    if (!(event instanceof CustomEvent)) return;
    if (event.detail.sender !== this.dataset.sender) {
      this.refs.stateSelect.value = event.detail.newState;
    }
    this.removeEmptyOption();
  }

  /**
   *
   * @param {Event} event
   */
  onStateChange({ target }) {
    if (!(target instanceof HTMLSelectElement)) return;
    const newState = target.value;
    window.localStorage.setItem('fgw_state', newState);
    globalThis.FGAvailabilityData.setCurrentState(newState);
    document.dispatchEvent(new CustomEvent('fgw_state_change', {
      detail: {
        newState: newState,
        sender: this.dataset.sender
      }
    }));
  }

  /**
   *
   * @param {Event} event
   */
  onSubmitClick({ target }) {
    if (!(target instanceof HTMLButtonElement)) return;
    this.setErrors([]);
    if (this.refs.stateSelect.value === "") {
      this.setErrors(['You must select a state']);
      return;
    }
    const now = Date.now();
    window.localStorage.setItem(this.ageCheckKey, now.toString());
    const newState = this.refs.stateSelect.value;
    window.localStorage.setItem('fgw_state', newState);
    globalThis.FGAvailabilityData.setCurrentState(newState);
    document.dispatchEvent(new CustomEvent('fgw_state_change', {
      detail: {
        newState: newState,
        sender: this.dataset.sender
      }
    }));
    this.hideDialog();
  }

  /**
   *
   * @param {Event} event
   */
  onCancelClick({ target }) {
    if (!(target instanceof HTMLButtonElement)) return;
    this.setErrors(['You must be 21 or older']);
  }
}

customElements.define('fg-state-age-dialog', FGStateAgeDialog);